import api from '../api/client';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Row,
    Col,
    Space,
    Typography,
    message,
    Divider,
    Alert,
    Steps,
    Modal,
    Table,
    Descriptions,
    Upload,
    AutoComplete,
    Spin,
    Radio
} from 'antd';
import {
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    InfoCircleOutlined,
    DollarOutlined,
    ShoppingOutlined,
    BarcodeOutlined,
    UploadOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useHistory } from 'react-router-dom';
import moment from 'moment';
import { fetchSuppliers, fetchUsers, fetchItems, createItem, searchItems, addInventory } from '../api';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import StakeholderModal from './StakeholderModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

const PurchasePage = () => {
    const [headerForm] = Form.useForm();
    const history = useHistory();
    const account = useSelector((state) => state.account.user);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [suppliers, setSuppliers] = useState([]);
    const [users, setUsers] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [itemsList, setItemsList] = useState([
        { type: null, category: null, item_id: null, tempName: '', quantity: 1, unit_cost: 0, total_cost: 0 }
    ]);
    const [suggestionsMap, setSuggestionsMap] = useState({});
    const [searchLoading, setSearchLoading] = useState(false);

    // Modals
    const [supplierModalVisible, setSupplierModalVisible] = useState(false);
    const [newSupplierName, setNewSupplierName] = useState('');
    const [newSupplierContact, setNewSupplierContact] = useState('');
    const [newSupplierPhone, setNewSupplierPhone] = useState('');

    const [itemModalVisible, setItemModalVisible] = useState(false);
    const [newItemForm] = Form.useForm();
    const [pendingItemLineIndex, setPendingItemLineIndex] = useState(null);
    const [pendingItemType, setPendingItemType] = useState(null);
    const [pendingItemCategory, setPendingItemCategory] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [deliveredMethod, setDeliveredMethod] = useState('delivered');
    const [selectedStakeholder, setSelectedStakeholder] = useState(null);
    const [stakeholderModalVisible, setStakeholderModalVisible] = useState(false);

    // Load data
    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                const data = await fetchSuppliers();
                setSuppliers(data);
            } catch (error) {
                console.error('Error loading suppliers:', error);
            }
        };
        const loadUsers = async () => {
            try {
                const data = await fetchUsers();
                setUsers(data);
            } catch (error) {
                console.error('Error loading users:', error);
            }
        };
        const loadItems = async () => {
            try {
                const data = await fetchItems();
                setAllItems(data);
            } catch (error) {
                console.error('Error loading items:', error);
            }
        };
        loadSuppliers();
        loadUsers();
        loadItems();
    }, []);

    // Helper functions
    const calculateTotal = (quantity, unitCost) => (quantity || 0) * (unitCost || 0);

    const handleLineChange = (index, field, value) => {
        const newList = [...itemsList];
        newList[index][field] = value;
        if (field === 'quantity' || field === 'unit_cost') {
            newList[index].total_cost = calculateTotal(newList[index].quantity, newList[index].unit_cost);
        }
        if (field === 'type' || field === 'category') {
            newList[index].item_id = null;
            newList[index].tempName = '';
        }
        setItemsList(newList);
        if (field === 'type' || field === 'category') {
            setSuggestionsMap((prev) => ({ ...prev, [index]: [] }));
        }
    };

    const addItemLine = () => {
        setItemsList([...itemsList, { type: null, category: null, item_id: null, tempName: '', quantity: 1, unit_cost: 0, total_cost: 0 }]);
    };

    const removeItemLine = (index) => {
        const newList = [...itemsList];
        newList.splice(index, 1);
        setItemsList(newList);
        const newSuggestions = { ...suggestionsMap };
        delete newSuggestions[index];
        setSuggestionsMap(newSuggestions);
    };

    const loadSuppliers = async () => {
        try {
            const data = await fetchSuppliers();
            setSuppliers(data);
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    const loadUsers = async () => {
        try {
            const data = await fetchUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };
    
    useEffect(() => {
        loadSuppliers();
        loadUsers();
        // loadItems();
    }, []);

    // Handle stakeholder added
    const handleStakeholderAdded = (newStakeholder) => {
        // setStakeholders((prev) => [...prev, newStakeholder]);
        loadSuppliers();
        setSuppliers((prev) => [...prev, newStakeholder]);
        setSelectedStakeholder(newStakeholder.id);
        headerForm.setFieldsValue({ stakeholderId: newStakeholder.id });
        message.success(`Supplier ${newStakeholder.name} added successfully`);
    };

    const handleAddSupplier = async () => {
        if (!newSupplierName.trim()) {
            message.warning('Please enter a supplier name');
            return;
        }
        try {
            const response = await api.post('/api/supplier', {
                name: newSupplierName.trim(),
                contact_person: newSupplierContact,
                phone: newSupplierPhone
            });
            const result = response.data;
            if (result.id) {
                setSuppliers([...suppliers, result]);
                message.success('Supplier added');
                form.setFieldsValue({ stakeholderId: result.id });
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            console.error('Error adding supplier:', error);
            message.error('Failed to add supplier');
        }
        setSupplierModalVisible(false);
        setNewSupplierName('');
        setNewSupplierContact('');
        setNewSupplierPhone('');
    };

    const isItemNameExists = (name, type, category) => {
        return allItems.some((item) => item.name.toLowerCase() === name.toLowerCase() && item.type === type && item.category === category);
    };

    const handleAddNewItem = async (values) => {
        const { name, unit } = values;
        const type = pendingItemType;
        const category = pendingItemCategory;

        if (isItemNameExists(name, type, category)) {
            message.warning(`Item "${name}" already exists. Please select it from the list.`);
            return;
        }

        try {
            const newItem = await createItem({ name, type, category, unit, initial_stock: 0 });
            message.success('Item created');
            const updatedItems = await fetchItems();
            setAllItems(updatedItems);
            if (pendingItemLineIndex !== null) {
                const newList = [...itemsList];
                newList[pendingItemLineIndex].item_id = newItem.id;
                newList[pendingItemLineIndex].tempName = newItem.name;
                setItemsList(newList);
                setPendingItemLineIndex(null);
            }
            setItemModalVisible(false);
            newItemForm.resetFields();
        } catch (error) {
            console.error('Error creating item:', error);
            message.error('Failed to create item');
        }
    };

    const fetchItemSuggestions = async (type, category, searchText) => {
        if (!type || !category || !searchText || searchText.length < 2) return [];
        try {
            const data = await searchItems(type, category, searchText);
            return data.map((item) => ({
                value: item.name,
                label: `${item.name} (${item.unit}) - Stock: ${item.current_stock} ${item.unit}`,
                item_id: item.id,
                unit: item.unit
            }));
        } catch (error) {
            console.error('Error fetching items:', error);
            return [];
        }
    };

    const handleSearch = async (idx, value) => {
        const line = itemsList[idx];
        if (!line.type || !line.category) return;
        if (value && value.length >= 2) {
            setSearchLoading(true);
            const suggestions = await fetchItemSuggestions(line.type, line.category, value);
            setSuggestionsMap((prev) => ({ ...prev, [idx]: suggestions }));
            setSearchLoading(false);
        } else {
            setSuggestionsMap((prev) => ({ ...prev, [idx]: [] }));
        }
    };

    const validateLines = () => {
        for (let i = 0; i < itemsList.length; i++) {
            const line = itemsList[i];
            if (!line.type) {
                message.error(`Line ${i + 1}: Please select a product type`);
                return false;
            }
            if (!line.category) {
                message.error(`Line ${i + 1}: Please select a category`);
                return false;
            }
            if (!line.item_id) {
                message.error(`Line ${i + 1}: Please select a product name`);
                return false;
            }
            if (line.quantity <= 0) {
                message.error(`Line ${i + 1}: Quantity must be positive`);
                return false;
            }
            if (line.unit_cost <= 0) {
                message.error(`Line ${i + 1}: Unit cost must be positive`);
                return false;
            }
        }
        return true;
    };

    const totalRestockValue = itemsList.reduce((sum, line) => sum + (line.total_cost || 0), 0);
    const totalQuantity = itemsList.reduce((sum, line) => sum + (line.quantity || 0), 0);

    const getImageFilenames = () => fileList.map((file) => file.name);

    const handleSubmit = async () => {
        if (!validateLines()) return;
        try {
            setLoading(true);
            const formData = form.getFieldsValue();
            console.log('Validated values:', formData);
            const headerData = {
                stakeholderId: formData.stakeholderId,
                paymentMethod: 'unpaid',
                paymentStatus: 'unpaid',
                totalCost: totalRestockValue,
                notes: formData.notes,
                remarks: formData.invoice_number,
                // inventoryType: 'restock',
                delivery: formData.deliveredMethod,
                delGuy: formData.deliveryPersonId,
                servedBy: formData.servedBy,
                pay_reference: formData.pay_reference,
                inventoryType: formData.transactionType || 'restock', // added field
                userId: account.id,
                userName: account.firstname,
                recorded: formData.date ? dayjs(formData.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD HH:mm:ss'),
                images: JSON.stringify(getImageFilenames()),
                // remarks: formData.remarks,
                dueDate: formData.dueDate ? dayjs(formData.dueDate).format('YYYY-MM-DD') : null,

                quantity: totalQuantity
            };
            const itemsData = itemsList.map((line) => ({
                item_id: line.item_id,
                quantity: line.quantity,
                unit_cost: line.unit_cost,
                total_cost: line.total_cost
            }));

            const result = await addInventory({ header: headerData, items: itemsData });
            if (result.success) {
                message.success('Inventory recorded successfully');
                history.push('/inventory');
                form.resetFields();
                setFileList([]);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error saving inventory:', error);
            if (error.errorFields) {
                message.error(`Validation error: ${error.errorFields.map((f) => f.name.join(',')).join(', ')}`);
            } else {
                message.error('Failed to save inventory: ' + (error.message || 'Unknown error'));
            }
        } finally {
            setLoading(false);
        }
    };

    const nextStep = async () => {
        try {
            if (currentStep === 0) {
                await form.validateFields(['stakeholderId', 'invoice_number', 'paymentMethod', 'date', 'servedBy', 'transactionType']);
            }

            setCurrentStep((prev) => prev + 1);
        } catch (err) {}
    };

    const prevStep = () => setCurrentStep(currentStep - 1);

    const steps = [
        { title: 'Invoice Info', icon: <InfoCircleOutlined /> },
        { title: 'Items', icon: <ShoppingOutlined /> },
        { title: 'Review', icon: <DollarOutlined /> }
    ];

    const categoryOptions = [
        { value: 'pig', label: 'Pig' },
        { value: 'poultry', label: 'Poultry' },
        { value: 'goat', label: 'Goat' },
        { value: 'sheep', label: 'Sheep' },
        { value: 'weaner', label: 'Weaner' },
        { value: 'pig-weaner', label: 'Pig Weaner' },
        { value: 'goat-weaner', label: 'Goat Weaner' },
        { value: 'sheep-weaner', label: 'Sheep Weaner' },
        { value: 'general', label: 'General' }
    ];

    const unitOptionsByType = {
        feed: [
            { label: 'Kilograms (kg)', value: 'kg' },
            { label: 'Grams (g)', value: 'g' },
            { label: 'Bags', value: 'bags' }
        ],
        vaccine: [
            { label: 'Milliliters (ml)', value: 'ml' },
            { label: 'Liters (L)', value: 'liters' },
            { label: 'Doses', value: 'doses' },
            { label: 'Vials', value: 'vials' }
        ],
        water: [
            { label: 'Liters (L)', value: 'liters' },
            { label: 'Milliliters (ml)', value: 'ml' },
            { label: 'Cubic Meters (m³)', value: 'm3' }
        ],
        supplement: [
            { label: 'Grams (g)', value: 'g' },
            { label: 'Kilograms (kg)', value: 'kg' },
            { label: 'Milliliters (ml)', value: 'ml' },
            { label: 'Liters (L)', value: 'liters' },
            { label: 'Tablets', value: 'tablets' },
            { label: 'Packets', value: 'packets' }
        ],
        food: [
            { label: 'Kilograms (kg)', value: 'kg' },
            { label: 'Grams (g)', value: 'g' },
            { label: 'Plates', value: 'plates' },
            { label: 'Portions', value: 'portions' }
        ]
    };

    useEffect(() => {
        form.setFieldsValue({
            type: pendingItemType,
            unit: undefined // reset unit when type changes
        });
    }, [pendingItemType]);

    const getItemUnit = (itemId) => {
        const item = allItems.find((i) => i.id === itemId);
        return item ? item.unit : '';
    };

    const getItemName = (itemId) => {
        const item = allItems.find((i) => i.id === itemId);
        return item ? item.name : '';
    };

    const paymentOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'unpaid', label: 'Credit' }
    ];

    return (
        <>
            <Card style={{ marginBottom: 24 }}>
                <Steps current={currentStep}>
                    {steps.map((step, idx) => (
                        <Step key={idx} title={step.title} icon={step.icon} />
                    ))}
                </Steps>
            </Card>

            <Card
                title="New Stock In (Restock)"
                style={{ marginBottom: 24 }}
                extra={
                    <Link to="/inventory">
                        <Button icon={<ArrowLeftOutlined />}>Back to Inventory</Button>
                    </Link>
                }
            >
                <Form
                    form={form}
                    preserve
                    layout="vertical"
                    initialValues={{
                        date: dayjs(), // ✅ not moment()
                        paymentStatus: 'unpaid',
                        invoiced: false,
                        servedBy: account.id
                    }}
                    onValuesChange={(_, allValues) => {
                        form.setFieldsValue(allValues); // 🔥 keeps state alive
                    }}
                >
                    <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                        <Title level={4}>Invoice Details</Title>
                        {/* New field: Transaction Type */}
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Transaction Type"
                                    name="transactionType"
                                    rules={[{ required: true, message: 'Please select transaction type' }]}
                                >
                                    <Radio.Group>
                                        <Radio value="restock">Restock (Existing Items)</Radio>
                                        <Radio value="new_stock">New Stock (First-time Purchase)</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={24}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Supplier" name="stakeholderId" rules={[{ required: true }]} preserve={true}>
                                    <Select
                                        showSearch
                                        placeholder="Select supplier"
                                        optionFilterProp="children"
                                        value={selectedStakeholder}
                                        onChange={(value) => {
                                            setSelectedStakeholder(value);
                                            headerForm.setFieldsValue({ stakeholderId: value });
                                        }}
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                <Divider style={{ margin: '4px 0' }} />
                                                <div
                                                    style={{
                                                        padding: '4px 8px',
                                                        color: '#1677ff',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8
                                                    }}
                                                    onClick={() => setStakeholderModalVisible(true)}
                                                >
                                                    <PlusOutlined />
                                                    <span>Add supplier</span>
                                                </div>
                                            </>
                                        )}
                                    >
                                        {suppliers.map((s) => (
                                            <Option key={s.id} value={s.id}>
                                                {s.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {/* </Row>
                        <Row gutter={24}> */}
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Invoice Number"
                                    name="invoice_number"
                                    rules={[{ required: true, message: 'Please enter invoice number' }]}
                                >
                                    <Input placeholder="INV-001" prefix={<BarcodeOutlined />} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item label="Date" name="date" rules={[{ required: true }]}>
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={24}>
                            <Col xs={24} md={6}>
                                <Space direction="horizontal" style={{ width: '100%' }} align="start">
                                    <Form.Item
                                        name="deliveredMethod"
                                        label="Delivery Method"
                                        // rules={[{ required: true, message: 'Please select delivery method' }]}
                                    >
                                        <Select onChange={(value) => setDeliveredMethod(value)}>
                                            <Select.Option value="delivered">Delivered</Select.Option>
                                            {/* <Select.Option value="picked">Picked at Farm</Select.Option> */}
                                        </Select>
                                    </Form.Item>

                                    {deliveredMethod === 'delivered' && (
                                        <Form.Item
                                            name="deliveryPersonId"
                                            label="Delivery Person"
                                            // rules={[{ required: true, message: 'Please select delivery person' }]}
                                        >
                                            <Select placeholder="Select delivery person" showSearch optionFilterProp="children">
                                                {users.map((e) => (
                                                    <Select.Option key={e.id} value={e.id}>
                                                        {e.firstname} {e.lastname}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                </Space>
                            </Col>
                            {/* </Row>

                        <Row gutter={24}> */}
                            <Col xs={24} md={6}>
                                <Form.Item label="Due Date" name="dueDate">
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item label="Reference / Payment Ref" name="pay_reference">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item
                                    name="servedBy"
                                    label="Served By"
                                    rules={[{ required: true, message: 'Please select a person' }]}
                                >
                                    <Select placeholder="Select a person" showSearch optionFilterProp="children">
                                        {users.map((e) => (
                                            <Select.Option key={e.id} value={e.id}>
                                                {e.firstname} {e.lastname}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item label="Notes / Remarks" name="notes">
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider />
                        <Form.Item label="Upload Files">
                            <Upload
                                listType="picture"
                                beforeUpload={() => false}
                                multiple
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                            >
                                <Button icon={<UploadOutlined />}>Upload</Button>
                            </Upload>
                        </Form.Item>
                    </div>
                    <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                        <Title level={4}>Items Received</Title>
                        {itemsList.map((line, idx) => (
                            <div key={idx} style={{ marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} md={3}>
                                        <Select
                                            placeholder="Product Type"
                                            value={line.type}
                                            onChange={(val) => handleLineChange(idx, 'type', val)}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="feed">Feed</Option>
                                            <Option value="food">Food</Option>
                                            <Option value="vaccine">Vaccine</Option>
                                            <Option value="water">Water</Option>
                                            <Option value="supplement">Supplement</Option>
                                            <Option value="other">Other</Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} md={3}>
                                        <Select
                                            placeholder="Category"
                                            value={line.category}
                                            onChange={(val) => handleLineChange(idx, 'category', val)}
                                            style={{ width: '100%' }}
                                            disabled={!line.type}
                                        >
                                            {categoryOptions.map((cat) => (
                                                <Option key={cat.value} value={cat.value}>
                                                    {cat.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <AutoComplete
                                            placeholder="Type product name"
                                            value={line.tempName}
                                            onChange={(val) => handleLineChange(idx, 'tempName', val)}
                                            onSearch={(val) => handleSearch(idx, val)}
                                            onSelect={(val, option) => {
                                                if (option.item_id) {
                                                    handleLineChange(idx, 'item_id', option.item_id);
                                                    handleLineChange(idx, 'tempName', option.value);
                                                } else if (option.key === 'create') {
                                                    setPendingItemLineIndex(idx);
                                                    setPendingItemType(line.type);
                                                    setPendingItemCategory(line.category);
                                                    newItemForm.setFieldsValue({
                                                        name: line.tempName,
                                                        type: line.type,
                                                        category: line.category,
                                                        unit: ''
                                                    });
                                                    setItemModalVisible(true);
                                                }
                                            }}
                                            style={{ width: '100%' }}
                                            disabled={!line.type || !line.category}
                                            filterOption={false}
                                            notFoundContent={searchLoading ? <Spin size="small" /> : null}
                                        >
                                            {suggestionsMap[idx]?.map((s) => (
                                                <Option key={s.item_id} value={s.value} item_id={s.item_id}>
                                                    {s.label}
                                                </Option>
                                            ))}
                                            {line.tempName && line.tempName.length >= 2 && suggestionsMap[idx]?.length === 0 && (
                                                <Option key="create" value={line.tempName} style={{ color: '#24ac78' }}>
                                                    <PlusOutlined /> Create new "{line.tempName}"
                                                </Option>
                                            )}
                                        </AutoComplete>
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <InputNumber
                                            placeholder="Quantity"
                                            value={line.quantity}
                                            onChange={(val) => handleLineChange(idx, 'quantity', val)}
                                            style={{ width: '100%' }}
                                            min={0.01}
                                            step={0.01}
                                        />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <InputNumber
                                            placeholder="Unit Cost (KES)"
                                            value={line.unit_cost}
                                            onChange={(val) => handleLineChange(idx, 'unit_cost', val)}
                                            style={{ width: '100%' }}
                                            min={0}
                                            step={0.01}
                                            formatter={(v) => `KES ${v}`}
                                            parser={(v) => v.replace('KES ', '')}
                                        />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <InputNumber
                                            placeholder="Total"
                                            value={line.total_cost}
                                            readOnly
                                            style={{ width: '100%' }}
                                            formatter={(v) => `KES ${v}`}
                                        />
                                    </Col>
                                    <Col xs={12} md={2}>
                                        {itemsList.length > 1 && (
                                            <Button icon={<DeleteOutlined />} danger onClick={() => removeItemLine(idx)}>
                                                Remove
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                                {line.item_id && (
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                                        Unit: {getItemUnit(line.item_id)} | Selected: {getItemName(line.item_id)}
                                    </div>
                                )}
                            </div>
                        ))}
                        <Button type="dashed" icon={<PlusOutlined />} onClick={addItemLine} style={{ width: '100%', marginTop: 16 }}>
                            Add Item
                        </Button>
                    </div>
                    <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                        <Title level={4}>Review & Submit</Title>
                        <Alert
                            message="Please review the details below before submitting"
                            type="info"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                        <Descriptions title="Invoice Summary" bordered column={2}>
                            <Descriptions.Item label="Supplier">
                                {suppliers.find((s) => s.id === form.getFieldValue('stakeholderId'))?.name || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Invoice Number">{form.getFieldValue('invoice_number') || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Payment Method">{form.getFieldValue('paymentMethod') || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Date">{form.getFieldValue('date')?.format('DD/MM/YYYY') || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Due Date">
                                {form.getFieldValue('dueDate')?.format('DD/MM/YYYY') || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Payment Status">{form.getFieldValue('paymentStatus') || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Paid Amount">
                                KES {(form.getFieldValue('paidAmount') || 0).toFixed(2)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Due Date">
                                {form.getFieldValue('dueDate')?.format('DD/MM/YYYY') || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Served By">{form.getFieldValue('servedBy') || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Notes">{form.getFieldValue('notes') || '—'}</Descriptions.Item>
                        </Descriptions>
                        <Divider />
                        <Title level={5}>Items</Title>
                        <Table
                            dataSource={itemsList}
                            columns={[
                                { title: 'Type', dataIndex: 'type' },
                                { title: 'Category', dataIndex: 'category' },
                                { title: 'Item', dataIndex: 'item_id', render: (id) => getItemName(id) },
                                { title: 'Quantity', dataIndex: 'quantity' },
                                { title: 'Unit Cost (KES)', dataIndex: 'unit_cost', render: (v) => v?.toFixed(2) },
                                { title: 'Total (KES)', dataIndex: 'total_cost', render: (v) => v?.toFixed(2) }
                            ]}
                            pagination={false}
                            size="small"
                        />
                        <div style={{ textAlign: 'right', marginTop: 16 }}>
                            <Text strong>Total Invoice Value: </Text>
                            <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                                KES {totalRestockValue.toFixed(2)}
                            </Text>
                        </div>
                    </div>

                    <Divider />

                    <Row justify="space-between">
                        <Col>{currentStep > 0 && <Button onClick={prevStep}>Previous</Button>}</Col>
                        <Col>
                            {currentStep < steps.length - 1 ? (
                                <Button type="primary" onClick={nextStep}>
                                    Next Step
                                </Button>
                            ) : (
                                <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSubmit}>
                                    Submit Inventory
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Add Supplier Modal */}
            <Modal title="Add New Supplier" open={supplierModalVisible} onCancel={() => setSupplierModalVisible(false)} footer={null}>
                <Form layout="vertical">
                    <Form.Item label="Supplier Name" required>
                        <Input value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} />
                    </Form.Item>
                    <Form.Item label="Contact Person">
                        <Input value={newSupplierContact} onChange={(e) => setNewSupplierContact(e.target.value)} />
                    </Form.Item>
                    <Form.Item label="Phone">
                        <Input value={newSupplierPhone} onChange={(e) => setNewSupplierPhone(e.target.value)} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleAddSupplier}>
                            Add Supplier
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add New Item Modal */}
            <Modal title="Add New Item" open={itemModalVisible} onCancel={() => setItemModalVisible(false)} footer={null}>
                <Form form={newItemForm} onFinish={handleAddNewItem} layout="vertical">
                    <Form.Item name="name" label="Item Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" hidden initialValue={pendingItemType}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="category" hidden initialValue={pendingItemCategory}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="unit" label="Unit" rules={[{ required: true, message: 'Please select a unit' }]}>
                        <Select placeholder="Select unit" disabled={!pendingItemType} options={unitOptionsByType[pendingItemType] || []} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Create Item
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Stakeholder Modal */}
            <StakeholderModal
                visible={stakeholderModalVisible}
                onCancel={() => setStakeholderModalVisible(false)}
                onSuccess={handleStakeholderAdded}
            />
        </>
    );
};

export default PurchasePage;
